import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  CreateRecurringExpense,
  RecurringExpenseListQuery,
  UpdateRecurringExpense,
} from '@trellis/shared'

import {
  activateRecurringExpense,
  cancelRecurringExpense,
  createRecurringExpense,
  deleteRecurringExpense,
  getRecurringExpense,
  getRecurringExpenseStats,
  listRecurringExpenseRuns,
  listRecurringExpenses,
  pauseRecurringExpense,
  triggerRecurringExpense,
  updateRecurringExpense,
} from '@/requests/recurring-expenses'

export function useRecurringExpensesQuery(options?: RecurringExpenseListQuery) {
  return useQuery({
    queryKey: ['recurring-expenses', options],
    queryFn: () => listRecurringExpenses(options ?? {}),
    enabled: options !== undefined,
  })
}

export function useRecurringExpenseStatsQuery(enabled = true) {
  return useQuery({
    queryKey: ['recurring-expenses', 'stats'],
    queryFn: getRecurringExpenseStats,
    enabled,
  })
}

export function useRecurringExpenseQuery(id: string) {
  return useQuery({
    queryKey: ['recurring-expenses', id],
    queryFn: () => getRecurringExpense(id),
    enabled: !!id,
  })
}

export function useRecurringExpenseRunsQuery(id: string) {
  return useQuery({
    queryKey: ['recurring-expenses', id, 'runs'],
    queryFn: () => listRecurringExpenseRuns(id),
    enabled: !!id,
  })
}

export function useCreateRecurringExpenseMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: CreateRecurringExpense) => {
      const result = await createRecurringExpense(data)
      if (result.error) throw new Error(result.error.message)
      return result
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['recurring-expenses'] })
    },
  })
}

export function useUpdateRecurringExpenseMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateRecurringExpense }) => {
      const result = await updateRecurringExpense(id, data)
      if (result.error) throw new Error(result.error.message)
      return result
    },
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({ queryKey: ['recurring-expenses'] })
      await queryClient.invalidateQueries({
        queryKey: ['recurring-expenses', variables.id],
      })
    },
  })
}

export function useActivateRecurringExpenseMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const result = await activateRecurringExpense(id)
      if (result.error) throw new Error(result.error.message)
      return result
    },
    onSuccess: async (_, id) => {
      await queryClient.invalidateQueries({ queryKey: ['recurring-expenses'] })
      await queryClient.invalidateQueries({ queryKey: ['recurring-expenses', id] })
    },
  })
}

export function usePauseRecurringExpenseMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const result = await pauseRecurringExpense(id)
      if (result.error) throw new Error(result.error.message)
      return result
    },
    onSuccess: async (_, id) => {
      await queryClient.invalidateQueries({ queryKey: ['recurring-expenses'] })
      await queryClient.invalidateQueries({ queryKey: ['recurring-expenses', id] })
    },
  })
}

export function useCancelRecurringExpenseMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const result = await cancelRecurringExpense(id)
      if (result.error) throw new Error(result.error.message)
      return result
    },
    onSuccess: async (_, id) => {
      await queryClient.invalidateQueries({ queryKey: ['recurring-expenses'] })
      await queryClient.invalidateQueries({ queryKey: ['recurring-expenses', id] })
    },
  })
}

export function useTriggerRecurringExpenseMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const result = await triggerRecurringExpense(id)
      if (result.error) throw new Error(result.error.message)
      return result
    },
    onSuccess: async (_, id) => {
      await queryClient.invalidateQueries({ queryKey: ['recurring-expenses'] })
      await queryClient.invalidateQueries({ queryKey: ['recurring-expenses', id] })
      await queryClient.invalidateQueries({
        queryKey: ['recurring-expenses', id, 'runs'],
      })
      await queryClient.invalidateQueries({ queryKey: ['expenses'] })
    },
  })
}

export function useDeleteRecurringExpenseMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const result = await deleteRecurringExpense(id)
      if (result.error) throw new Error(result.error.message)
      return result
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['recurring-expenses'] })
    },
  })
}
